import Phaser from 'phaser';
import { COLORS } from '../colors';
import { GAME_HEIGHT, GAME_WIDTH } from '../grid';

/**
 * Full-screen celebratory overlay shown when the student solves the FINAL level
 * (i.e. completes the whole game). Veils the workspace, congratulates the
 * student, gently pulses the headline, sprinkles a little procedural confetti,
 * and offers a single "PLAY AGAIN" button that routes back to Level 1.
 *
 * Rendered ENTIRELY above the rest of the UI (depth >= 100 — above the NEXT
 * STEP button at 60 and the drag ghost at 80). All visuals are procedural,
 * matching the rest of the game (rounded panels + Arial Black text, color
 * constants from `colors.ts`).
 *
 * Lifecycle: construct once (with the scene + an `onReplay` callback), then
 * `show()` / `hide()` to toggle. `show()` is idempotent — calling it while
 * already visible is a no-op, so the once-per-solve guard in GameScene plus
 * this internal guard both protect against double-spawning confetti/tweens.
 */
export class CompletionOverlay {
  private container: Phaser.GameObjects.Container;
  /** Independent hit zone at world coords (Phaser Container hit-area quirk — see CLAUDE.md). */
  private replayHit: Phaser.GameObjects.Zone;
  /** The big pulsing headline, kept so show()/hide() can drive its tween. */
  private headline!: Phaser.GameObjects.Text;
  private headlineTween?: Phaser.Tweens.Tween;
  private confetti: Phaser.GameObjects.Rectangle[] = [];
  private confettiTweens: Phaser.Tweens.Tween[] = [];
  private visible = false;

  // Replay button geometry (shared between the drawn visual and the world hit zone).
  private readonly btnW = 260;
  private readonly btnH = 70;
  private readonly btnY = GAME_HEIGHT / 2 + 150;

  constructor(
    private scene: Phaser.Scene,
    private onReplay: () => void,
  ) {
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;

    this.container = scene.add.container(0, 0);
    this.container.setDepth(100);

    // Dim/veil backdrop covering the full canvas. Interactive so taps behind
    // the overlay can't reach the workspace while it's up.
    const veil = scene.add.graphics();
    veil.fillStyle(0x000000, 0.72);
    veil.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    veil.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT),
      Phaser.Geom.Rectangle.Contains,
    );
    this.container.add(veil);

    // Central celebratory panel (rounded, shadowed — same idiom as Clipboard).
    const panelW = 720;
    const panelH = 380;
    const panelX = cx - panelW / 2;
    const panelY = cy - panelH / 2;
    const panel = scene.add.graphics();
    panel.fillStyle(0x000000, 0.45);
    panel.fillRoundedRect(panelX + 6, panelY + 8, panelW, panelH, 20);
    panel.fillStyle(COLORS.legendBg, 1);
    panel.fillRoundedRect(panelX, panelY, panelW, panelH, 20);
    panel.lineStyle(4, COLORS.buttonStartFill, 1);
    panel.strokeRoundedRect(panelX, panelY, panelW, panelH, 20);
    this.container.add(panel);

    // Headline — big, friendly, gently pulsing.
    const headline = scene.add
      .text(cx, cy - 90, 'You did it!', {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize: '72px',
        color: this.hexColor(COLORS.buttonStartFill),
      })
      .setOrigin(0.5, 0.5);
    this.container.add(headline);

    // Sub-message — celebrates finishing the whole game.
    const sub = scene.add
      .text(cx, cy - 10, 'You connected every body system\nand kept the whole body running!', {
        fontFamily: 'Arial, sans-serif',
        fontSize: '26px',
        color: '#222222',
        align: 'center',
        lineSpacing: 8,
      })
      .setOrigin(0.5, 0.5);
    this.container.add(sub);

    // PLAY AGAIN button (visual). Hit detection lives on a separate world-coords
    // Zone (added below) to dodge the Container hit-area quirks documented in
    // CLAUDE.md.
    const btn = scene.add.graphics();
    btn.fillStyle(0x000000, 0.4);
    btn.fillRoundedRect(
      cx - this.btnW / 2 + 3,
      this.btnY - this.btnH / 2 + 4,
      this.btnW,
      this.btnH,
      12,
    );
    btn.fillStyle(COLORS.buttonStartFill, 1);
    btn.fillRoundedRect(cx - this.btnW / 2, this.btnY - this.btnH / 2, this.btnW, this.btnH, 12);
    btn.lineStyle(3, 0x000000, 0.4);
    btn.strokeRoundedRect(cx - this.btnW / 2, this.btnY - this.btnH / 2, this.btnW, this.btnH, 12);
    this.container.add(btn);

    const btnText = scene.add
      .text(cx, this.btnY, 'PLAY AGAIN', {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize: '30px',
        color: this.hexColor(COLORS.buttonStartText),
      })
      .setOrigin(0.5, 0.5);
    this.container.add(btnText);

    // World-coords hit zone (NOT added to the container — same pattern as
    // TopBar's NEXT STEP button). Gated on `this.visible` so it ignores taps
    // while hidden.
    this.replayHit = scene.add
      .zone(cx, this.btnY, this.btnW, this.btnH)
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });
    this.replayHit.setDepth(101);
    this.replayHit.on('pointerdown', () => {
      if (this.visible) this.onReplay();
    });

    // Keep references to pulse the headline on show().
    this.headline = headline;

    this.container.setVisible(false);
    this.replayHit.disableInteractive();
  }

  /** Reveal the overlay, start the headline pulse, and sprinkle confetti. Idempotent. */
  show(): void {
    if (this.visible) return;
    this.visible = true;
    this.container.setVisible(true);
    this.replayHit.setInteractive({ useHandCursor: true });

    // Gentle headline pulse — scales between 1.0 and 1.08 forever.
    this.headlineTween = this.scene.tweens.add({
      targets: this.headline,
      scale: { from: 1, to: 1.08 },
      duration: 700,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    this.spawnConfetti();
  }

  /** Hide the overlay and tear down the pulse + confetti. Safe to call when already hidden. */
  hide(): void {
    this.visible = false;
    this.container.setVisible(false);
    this.replayHit.disableInteractive();
    this.headlineTween?.stop();
    this.headlineTween = undefined;
    this.headline.setScale(1);
    this.clearConfetti();
  }

  /** Permanently dispose all game objects (used if the scene tears down). */
  destroy(): void {
    this.clearConfetti();
    this.headlineTween?.stop();
    this.replayHit.destroy();
    this.container.destroy(true);
  }

  /**
   * A light scatter of procedural confetti: small colored rectangles that drift
   * down from the top of the panel area and fade out, then recycle. Drawn into
   * the overlay container so they sit above the panel and clean up with it.
   */
  private spawnConfetti(): void {
    const palette = [
      COLORS.molOxygen,
      COLORS.molFood,
      COLORS.molCo2,
      COLORS.buttonStartFill,
      COLORS.flowFill,
    ];
    const count = 28;
    for (let i = 0; i < count; i++) {
      const x = Phaser.Math.Between(40, GAME_WIDTH - 40);
      const startY = Phaser.Math.Between(-40, GAME_HEIGHT * 0.35);
      const color = palette[i % palette.length];
      const rect = this.scene.add.rectangle(
        x,
        startY,
        Phaser.Math.Between(8, 14),
        Phaser.Math.Between(8, 14),
        color,
      );
      rect.setAngle(Phaser.Math.Between(0, 360));
      this.container.add(rect);
      this.confetti.push(rect);

      const tween = this.scene.tweens.add({
        targets: rect,
        y: GAME_HEIGHT + 40,
        angle: rect.angle + Phaser.Math.Between(180, 540),
        duration: Phaser.Math.Between(2600, 4200),
        delay: Phaser.Math.Between(0, 1600),
        ease: 'Sine.easeIn',
        repeat: -1,
        repeatDelay: Phaser.Math.Between(0, 1200),
        onRepeat: () => {
          // Recycle to a fresh top position each loop so the fall feels endless.
          rect.x = Phaser.Math.Between(40, GAME_WIDTH - 40);
          rect.y = Phaser.Math.Between(-40, -10);
        },
      });
      this.confettiTweens.push(tween);
    }
  }

  private clearConfetti(): void {
    for (const t of this.confettiTweens) t.stop();
    this.confettiTweens = [];
    for (const r of this.confetti) r.destroy();
    this.confetti = [];
  }

  private hexColor(n: number): string {
    return '#' + n.toString(16).padStart(6, '0');
  }
}
